/**
 * HOTEL-SVC-1 · Step 3 of the Create-Hotel wizard.
 *
 * Super Admin has just created the hotel (Step 1) and its administrator
 * (Step 2). Now they pick which optional services this property offers.
 * Core modules (Front Desk, Reservations, Housekeeping, Maintenance,
 * Finance) are always on — they don't appear here.
 *
 * On save, the hotel is fully live and the wizard closes.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, ShieldCheck, Loader2, Check } from "lucide-react";
import api from "@/lib/api";
import HotelServicesEditor from "@/pages/hotels/HotelServicesEditor";

export default function CreateHotelStep3() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/platform/v1/hotels/${hotelId}`);
        setHotel(data);
      } catch {
        toast.error("Failed to load hotel");
      } finally {
        setLoading(false);
      }
    })();
  }, [hotelId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading hotel…
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl" data-testid="create-hotel-step3-success">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-8 shadow-sm">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mb-4" />
          <h1 className="text-2xl font-semibold text-slate-900">
            Hotel is fully configured.
          </h1>
          <p className="text-slate-700 mt-2">
            <strong>{hotel?.name}</strong> is now live and its services have
            been set. Enabled services: <span className="font-medium">{done.enabledList.join(", ") || "—"}</span>.
          </p>
          <div className="mt-6 flex gap-3">
            <button
              data-testid="create-hotel-step3-back-btn"
              onClick={() => navigate("/platform/hotels")}
              className="px-4 py-2.5 rounded-lg bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 shadow-sm"
            >
              Back to Hotels
            </button>
            <button
              onClick={() => navigate(`/platform/hotels/${hotelId}`)}
              className="px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium"
            >
              View Hotel Detail
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-16" data-testid="create-hotel-step3">
      {/* Progress rail — follows platform amber accent convention */}
      <div className="flex items-center gap-3 text-[12px] font-medium">
        <StepDot n={1} label="Hotel"         state="done" />
        <Rail done />
        <StepDot n={2} label="Administrator" state="done" />
        <Rail done />
        <StepDot n={3} label="Services"      state="active" />
      </div>

      {/* Header — same pattern as PlatformHotels / PlatformDashboard */}
      <header>
        <div className="text-[12px] uppercase tracking-[0.14em] text-amber-600 font-semibold">
          Step 3 of 3 · Services
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-1">
          What does <span className="text-amber-600">{hotel?.name}</span> offer?
        </h1>
        <p className="text-slate-600 mt-2 text-sm max-w-2xl">
          Tap the services this property provides. Core modules — Front Desk,
          Reservations, Housekeeping, Maintenance and Finance — are always on
          and don&apos;t appear here. You can revisit this any time from
          <span className="text-slate-800 font-medium"> Automation → Hotel Services</span>.
        </p>
      </header>

      {/* Services grid on a plain white card matching platform elsewhere */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 shadow-sm">
        <HotelServicesEditor
          hotelId={hotelId}
          title=""
          embedded
        />
      </div>

      {/* Footer actions — plain amber CTA to match Create Hotel / Create Admin */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => navigate("/platform/hotels")}
          className="text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
        >
          Skip — I&apos;ll configure services later
        </button>
        <FinishButton hotelId={hotelId} setDone={setDone} />
      </div>
    </div>
  );
}


function StepDot({ n, label, state }) {
  const isDone   = state === "done";
  const isActive = state === "active";
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border transition-all
          ${isDone
            ? "bg-amber-500 text-white border-amber-500"
            : isActive
              ? "bg-white text-amber-700 border-amber-500 ring-2 ring-amber-200"
              : "bg-white text-slate-400 border-slate-200"}`}
      >
        {isDone ? <Check size={16} strokeWidth={2} /> : n}
      </span>
      <span className={
        isActive ? "text-slate-900 font-semibold"
        : isDone ? "text-slate-600"
        : "text-slate-400"
      }>
        {label}
      </span>
    </span>
  );
}

function Rail({ done }) {
  return (
    <span className={`h-[2px] flex-1 max-w-[80px] rounded-full ${done ? "bg-amber-300" : "bg-slate-200"}`} />
  );
}


function FinishButton({ hotelId, setDone }) {
  const [saving, setSaving] = useState(false);

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.get(`/hotels/v1/${hotelId}/services`);
      const enabledList = Object.entries(data?.services || {})
        .filter(([, v]) => v?.enabled)
        .map(([k]) => k);
      setDone({ enabledList });
    } catch {
      toast.error("Failed to finalise services");
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      data-testid="create-hotel-step3-finish-btn"
      onClick={finish}
      disabled={saving}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {saving ? "Finalising…" : (
        <>
          Finish setup
          <ArrowRight className="w-4 h-4" />
        </>
      )}
    </button>
  );
}
